'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import ConditionalWrapper from '../utils/conditional-wrapper'
import { useLayout } from '../hooks'
import { TabordionItemType } from './tabordion'
import type { Props } from './tabordion'
import { useRouter } from 'next/navigation'
import { ContentSection, ContentWrapper, TabList, TabListItem } from './tabordion-components'

type TabordionTabs = {
  id: Props['id']
  tabTypeClassName: 'c-tabs' | 'c-mega-tabs'
  tabsClassName: Props['tabsClassName']
  vertical: Props['vertical']
  mega: Props['mega']
  noContentPadding: Props['noContentPadding']
  items: Props['items']
  activeTabs: number[]
  tabordionId: string
  sticky: Props['sticky']
  tabWidth: Props['tabWidth']
  links: Props['links']
  onTabClick: Props['onTabClick']
  toggleItem: (tab: number) => void
}
export default function TaboridonTabs({
  id,
  tabTypeClassName,
  tabsClassName,
  vertical,
  mega = false,
  noContentPadding = false,
  items,
  activeTabs,
  tabordionId,
  sticky,
  tabWidth = 'auto',
  links = false,
  onTabClick,
  toggleItem,
}: TabordionTabs) {
  return (
    <div
      id={id}
      className={classNames(tabTypeClassName, tabsClassName, {
        'c-tabs--vertical': vertical && !mega,
      })}>
      <TabsContent
        tabTypeClassName={tabTypeClassName}
        noContentPadding={noContentPadding}
        mega={mega}
        items={items}
        tabordionId={tabordionId}
        activeTabs={activeTabs}
        menu={
          <TabsMenu
            sticky={sticky}
            mega={mega}
            tabTypeClassName={tabTypeClassName}
            items={items}
            tabWidth={tabWidth}
            links={links}
            tabordionId={tabordionId}
            activeTabs={activeTabs}
            onTabClick={onTabClick}
            toggleItem={toggleItem}
          />
        }
      />
    </div>
  )
}
type TabsContentProps = {
  tabTypeClassName: string
  noContentPadding: boolean
  mega: boolean
  items: TabordionItemType[]
  tabordionId: string
  activeTabs: number[]
  menu: React.ReactNode
}

const TabsContent = ({
  tabTypeClassName,
  noContentPadding,
  mega,
  items,
  tabordionId,
  activeTabs,
  menu,
}: TabsContentProps) => (
  <ContentSection
    tabTypeClassName={tabTypeClassName}
    noContentPadding={noContentPadding}
    mega={mega}>
    {menu}
    {items.map((item, i) => (
      <ContentWrapper
        key={i}
        idx={i}
        tabordionId={tabordionId}
        isActive={activeTabs.includes(i)}
        tabTypeClassName={tabTypeClassName}>
        {item.content ? (
          item.content
        ) : (
          <div className="container u-py-24">{item.noContentText}</div>
        )}
      </ContentWrapper>
    ))}
  </ContentSection>
)
type TabsMenuProps = {
  sticky?: boolean
  mega: boolean
  tabTypeClassName: string
  items: TabordionItemType[]
  tabWidth: string
  links: boolean
  tabordionId: string
  activeTabs: number[]
  onTabClick: ((tabNumber: number) => void) | undefined
  toggleItem: (arg: number) => void
}

export const TabsMenu = ({
  sticky,
  mega,
  tabTypeClassName,
  items,
  tabWidth,
  links,
  tabordionId,
  activeTabs,
  onTabClick,
  toggleItem,
}: TabsMenuProps) => {
  const router = useRouter()

  const handleEnterRouteEvent = (
    { key }: React.KeyboardEvent<HTMLLIElement>,
    href: string | undefined
  ) => {
    if (!href || key !== 'Enter') {
      return
    }

    router.push(href)
  }

  return (
    <TabsMenuContainer mega={mega} sticky={sticky}>
      {/* because of header height and button zindex */}
      <TabList tabTypeClassName={tabTypeClassName} sticky={sticky}>
        {items.map((item, i) => (
          <TabListItem
            key={i}
            id={item.id}
            className={`${tabTypeClassName}__tab`}
            style={{ flexBasis: tabWidth }}
            onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) =>
              handleEnterRouteEvent(e, item.href)
            }>
            <ConditionalWrapper
              condition={links}
              wrapper={children => (
                <Link
                  prefetch={false}
                  href={item.href ?? ''}
                  shallow
                  legacyBehavior
                  scroll={item.scroll !== false}>
                  {children}
                </Link>
              )}>
              <TabItemType
                onClick={() => {
                  if (onTabClick) {
                    onTabClick(i)
                  }
                  toggleItem(i)
                }}
                href={item.href}
                links={links}
                id={`tab-${i}-${tabordionId}`}
                className={classNames(`${tabTypeClassName}__tab-btn`, {
                  [`${tabTypeClassName}__tab-btn--active`]: activeTabs.includes(i),
                  [`${tabTypeClassName}__tab-btn--link`]: links,
                })}
                aria-controls={`tab-pane-${i}-${tabordionId}`}>
                <span className="d-block u-h-100" data-bold-placeholder={item.title}>
                  {item.title}
                </span>
              </TabItemType>
            </ConditionalWrapper>
          </TabListItem>
        ))}
      </TabList>
    </TabsMenuContainer>
  )
}

export const TabsMenuContainer = ({
  children,
  mega,
  sticky,
}: {
  children: React.ReactNode
  mega: Props['mega']
  sticky: Props['sticky']
}) => {
  const [isTabMenuSticky, setIsTabMenuSticky] = useState(false)
  const { responsive } = useLayout()
  const { header } = useLayout()
  const tabsMenuRef = useRef<HTMLDivElement | null>(null)
  const handleScroll = useCallback(() => {
    const itemTop = tabsMenuRef.current?.getBoundingClientRect().top
    //threshold based on header height
    const isSticky = itemTop && itemTop > header.height.px && itemTop < header.height.px + 6

    setIsTabMenuSticky(!!isSticky)
  }, [tabsMenuRef?.current])
  // Attach the scroll listener to the div
  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])
  // The scroll listener

  return (
    <div
      className={classNames({
        container: mega && !sticky,
        'u-sticky u-bg-white': sticky,
        'u-box-shadow': isTabMenuSticky,
      })}
      ref={tabsMenuRef}
      style={
        sticky ? { top: responsive.breakpoints.min.LG ? '100.39px' : '105.39px', zIndex: 2 } : {}
      }>
      {children}
    </div>
  )
}

type TabItemProps = {
  links: boolean
  children: React.ReactNode
  id: string
  onClick: () => void
  className: string
  href?: string
}

const TabItemType = React.forwardRef(function TabItemType(props: TabItemProps, __) {
  if (!props) return null

  const { children, id, href, onClick, className } = { ...props }
  const componentProps = { children, id, onClick, className }
  const anchorProps = { ...componentProps, href }

  return props.links ? (
    <a {...anchorProps}>{children}</a>
  ) : (
    <button {...componentProps}>{children}</button>
  )
})
